// @flow
import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  loginMfaEmailSelector,
  loginMfaPhoneSelector,
  loginMfaCodeSentSelector,
  loginMfaVerifyingSelector,
  loginMfaErrorsSelector,
  loginMfaChannelSelector,
} from '../selectors';
import { sendMfaCode, verifyMfaCode } from '../actions';
import VerifyMfaChannelStep from './VerifyMfaChannelStep';
import VerifyMfaCodeStep from './VerifyMfaCodeStep';
import PageLoader from '../../common/components/PageLoader';
import './VerifyMfaPage.css';

const RESEND_SECONDS = 60;

type Props = {
  email: string,
  phone: ?string,
  codeSent: boolean,
  verifying: boolean,
  mfaChannel: ?string,
  sendMfaCode: (destination: string, channel: string) => void,
  verifyMfaCode: (email: string, code: string) => void,
};

const VerifyMfaPage = ({
  email,
  phone,
  codeSent,
  verifying,
  mfaChannel,
  sendMfaCode,
  verifyMfaCode,
  errors,
}: Props) => {
  const [channel, setChannel] = useState('sms');
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const inputsRef = useRef([]);
  const history = useHistory();
  const hasPhone = !!phone;

  useEffect(() => {
    if (mfaChannel) setChannel(mfaChannel);
  }, [mfaChannel]);

  useEffect(() => {
    if (!codeSent) return;
    setSecondsLeft(RESEND_SECONDS);
    const interval = setInterval(() => {
      setSecondsLeft(s => {
        if (s <= 1) { clearInterval(interval); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [codeSent]);

  
    useEffect(() => {
      if (!email) {
        history.push('/');
      }
    }, [email, history]);

  useEffect(() => {
    if (email && !hasPhone && !codeSent) {
      sendMfaCode(email, 'email');
    }
    // eslint-disable-next-line
  }, [email, hasPhone]);

  const formatTime = s => {
    const m = Math.floor(s / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  const handleChannelSubmit = e => {
    e.preventDefault();
    sendMfaCode(email, channel);
  };

  const handleDigitChange = (index, value) => {
    const cleaned = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    const next = [...digits];
    next[index] = cleaned;
    setDigits(next);
    if (cleaned && index < 5) inputsRef.current[index + 1]?.focus();
  };

  const handleDigitKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleDigitPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 6);
    const next = [...digits];

    if (!pasted) return;
    
    for (let i = 0; i < 6; i++) {
      next[i] = pasted[i] || '';
    }
    
    setDigits(next);
    const focusIndex = Math.min(pasted.length, 5);
    inputsRef.current[focusIndex]?.focus();
  };

  const handleCodeSubmit = e => {
    e.preventDefault();
    verifyMfaCode(email, digits.join(''));
  };

  const handleResend = () => {
    if (secondsLeft > 0) return;
    setDigits(['', '', '', '', '', '']);
    sendMfaCode(email, channel);
  };
  return (
    <div className="site-container">
      {codeSent ? (
        <VerifyMfaCodeStep
          channel={channel}
          email={email}
          digits={digits}
          inputsRef={inputsRef}
          onDigitChange={handleDigitChange}
          onDigitKeyDown={handleDigitKeyDown}
          onDigitPaste={handleDigitPaste}
          onSubmit={handleCodeSubmit}
          secondsLeft={secondsLeft}
          onResend={handleResend}
          formatTime={formatTime}
          verifying={verifying}
          errors={errors}
        />
      ) : hasPhone ? (
        <VerifyMfaChannelStep
          channel={channel}
          setChannel={setChannel}
          onSubmit={handleChannelSubmit}
          verifying={verifying}
        />
      ) : (
        <PageLoader text="Carregando..." />
      )}
    </div>
  );
};

const mapState = state => ({
  email: loginMfaEmailSelector(state),
  phone: loginMfaPhoneSelector(state),
  codeSent: loginMfaCodeSentSelector(state),
  verifying: loginMfaVerifyingSelector(state),
  errors: loginMfaErrorsSelector(state),
  mfaChannel: loginMfaChannelSelector(state),
});

const mapDispatch = dispatch => ({
  sendMfaCode: (email, channel) => dispatch(sendMfaCode(email, channel)),
  verifyMfaCode: (email, code) => dispatch(verifyMfaCode(email, code)),
});

export default connect(mapState, mapDispatch)(VerifyMfaPage);
